import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEOHead = ({ 
  title = 'Ivory Home Health Care',
  description = 'Ivory Home Health Care provides comprehensive home health services including skilled nursing, physical therapy, and medical care in the comfort of your home.',
  keywords = 'home health care, skilled nursing, physical therapy, medical home, healthcare, patient care',
  canonicalUrl,
  ogImage = '/og-image.png',
  ogType = 'website',
  twitterCard = 'summary_large_image',
  noIndex = false,
  structuredData
}) => {
  const baseUrl = window.location.origin;
  const fullCanonicalUrl = canonicalUrl ? `${baseUrl}${canonicalUrl}` : window.location.href;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonicalUrl} />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={`${baseUrl}${ogImage}`} />
      <meta property="og:site_name" content="Ivory Home Health Care" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={fullCanonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${baseUrl}${ogImage}`} />
      
      {/* Additional SEO tags */}
      <meta name="author" content="Ivory Healthcare Solutions" />
      <meta name="theme-color" content="#ea580c" />
      <meta name="msapplication-TileColor" content="#ea580c" />
      
      {/* Language */}
      <html lang="en" />
      
      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// Pre-configured SEO components for common pages
export const HomeSEO = () => (
  <SEOHead 
    title="Ivory Home Health Care - Quality Healthcare at Home"
    description="Ivory Home Health Care provides comprehensive home health services including skilled nursing, physical therapy, occupational therapy, and medical care in the comfort of your home. HIPAA compliant, Medicare certified."
    keywords="home health care, skilled nursing, physical therapy, occupational therapy, speech therapy, medical home, healthcare services, Medicare, Medicaid, HIPAA compliant"
    canonicalUrl="/"
    structuredData={{
      "@context": "https://schema.org",
      "@type": "MedicalOrganization",
      "name": "Ivory Home Health Care",
      "description": "Comprehensive home health services including skilled nursing and therapy",
      "url": window.location.origin,
      "logo": `${window.location.origin}/logo.png`,
      "medicalSpecialty": ["Home Health Care", "Skilled Nursing", "Physical Therapy"],
      "availableService": [
        {
          "@type": "MedicalProcedure",
          "name": "Skilled Nursing Care"
        },
        {
          "@type": "MedicalProcedure", 
          "name": "Physical Therapy"
        },
        {
          "@type": "MedicalProcedure",
          "name": "Occupational Therapy"
        }
      ]
    }}
  />
);

export const DashboardSEO = () => (
  <SEOHead 
    title="Dashboard - Ivory Home Health Care"
    description="Manage your home health care operations with the Ivory Health dashboard. View patient statistics, schedules, and clinical documentation."
    noIndex={true}
    canonicalUrl="/dashboard"
  />
);

export const PatientsSEO = () => (
  <SEOHead 
    title="Patient Management - Ivory Home Health Care"
    description="Manage patient records, demographics, and care plans in the Ivory Health patient management system."
    noIndex={true}
    canonicalUrl="/dashboard/patients"
  />
);

export const ClinicalSEO = () => (
  <SEOHead 
    title="Clinical Documentation - Ivory Home Health Care"
    description="Access OASIS assessments, nursing notes, and clinical documentation tools for home health care."
    noIndex={true}
    canonicalUrl="/dashboard/clinical"
  />
);

export const BillingSEO = () => (
  <SEOHead 
    title="Billing & Claims - Ivory Home Health Care"
    description="Manage billing, claims submission, and revenue cycle for home health care services."
    noIndex={true}
    canonicalUrl="/dashboard/billing"
  />
);

export const ReportsSEO = () => (
  <SEOHead 
    title="Reports & Analytics - Ivory Home Health Care"
    description="View comprehensive reports and analytics for home health care operations."
    noIndex={true}
    canonicalUrl="/dashboard/reports"
  />
);

export default SEOHead;
